'use client';

/**
 * SellerRfqsOpenCard — deadline-sorted open RFQ inbox (rail).
 *
 * Top-of-rail card for the seller landing. Surfaces the urgency
 * story (overdue / today / this-week / later) before the seller
 * picks anything from the explorer. Reads the same
 * `vendor_portal_rfq_list` strategy the explorer + landing card hit;
 * React Query dedupes the fetch.
 *
 * Click semantics: clicking an RFQ row advances the viewport
 * selection (same path as picking from the explorer's
 * `<VendorPortalExplorer />`). The matrix routes to
 * `mode5_quote_form` and the canvas paints the quote form.
 *
 * @see klyde_edge/ui_config/composition/cards/seller.py
 *      content_key="seller_rfqs_open"
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { useEffect, useMemo, useState } from 'react';
import { Inbox, Clock, AlertTriangle, Loader2 } from 'lucide-react';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useDevUserStore } from '@/lib/stores/dev-user-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useSellerLabel } from '@/lib/stores/total-matrix-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { api } from '@/lib/api/client';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { RailCard } from '@/components/copilot/rail-insights/rail-card';
import type { VPRfqSummary, VPRfqListResponse } from '@/lib/api/types';

type Urgency = 'overdue' | 'today' | 'soon' | 'later';

function urgencyOf(deadline: string | null): Urgency {
  if (!deadline) return 'later';
  const ms = Date.parse(deadline);
  if (Number.isNaN(ms)) return 'later';
  const now = Date.now();
  const dayMs = 24 * 60 * 60 * 1000;
  const diff = ms - now;
  if (diff < 0) return 'overdue';
  if (diff < dayMs) return 'today';
  if (diff < 7 * dayMs) return 'soon';
  return 'later';
}

const URGENCY_ORDER: Record<Urgency, number> = {
  overdue: 0,
  today: 1,
  soon: 2,
  later: 3,
};

export function SellerRfqsOpenCard() {
  const selectedUser = useDevUserStore((s) => s.selectedUser);
  const listStrategy = useCapabilityStore((s) => s.strategyName('vendor_portal', 'list'));
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const setExplorerSelection = useLayoutStore((s) => s.setExplorerSelection);

  // Server-driven labels.
  const labelLoading = useSellerLabel('rfqs_open_loading');
  const labelEmpty = useSellerLabel('rfqs_open_empty');

  const [rfqs, setRfqs] = useState<VPRfqSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!selectedUser || !listStrategy) return;
    let cancelled = false;
    setIsLoading(true);
    api
      .getStrategyData<VPRfqListResponse>(listStrategy, {})
      .then((res) => {
        if (cancelled || !res.success) return;
        setRfqs(res.data?.rfqs ?? []);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedUser, listStrategy]);

  const open = useMemo(() => {
    return rfqs
      .filter((r) => r.state === 'sent' || r.state === 'draft')
      .map((r) => ({ ...r, _urgency: urgencyOf(r.deadline) }))
      .sort((a, b) => {
        const u = URGENCY_ORDER[a._urgency] - URGENCY_ORDER[b._urgency];
        if (u !== 0) return u;
        const ta = a.deadline ? Date.parse(a.deadline) : Infinity;
        const tb = b.deadline ? Date.parse(b.deadline) : Infinity;
        return ta - tb;
      });
  }, [rfqs]);

  const overdueCount = open.filter((r) => r._urgency === 'overdue').length;
  const todayCount = open.filter((r) => r._urgency === 'today').length;

  return (
    <CardWithCount count={open.length} icon={Inbox}>
      {isLoading && open.length === 0 ? (
        <div className={cn(text.muted, 'flex items-center gap-1.5 px-3 py-2')}>
          <Loader2 className="h-3 w-3 animate-spin" />
          {labelLoading}
        </div>
      ) : open.length === 0 ? (
        <div className={cn(text.muted, 'px-3 py-2')}>{labelEmpty}</div>
      ) : (
        <>
          {(overdueCount > 0 || todayCount > 0) && (
            <UrgencyStrip
              overdueCount={overdueCount}
              todayCount={todayCount}
              className={cn(text.muted, 'px-3 pb-2 flex items-center gap-3')}
            />
          )}
          <div className="-mx-3 -mb-3">
            {open.slice(0, 8).map((rfq) => (
              <RfqRow
                key={rfq.rfq_sid}
                rfq={rfq}
                onClick={() => {
                  selectNode({ id: rfq.rfq_sid, label: rfq.reference ?? rfq.rfq_sid, type: 'rfq' });
                  setExplorerSelection('rfq');
                }}
              />
            ))}
          </div>
        </>
      )}
    </CardWithCount>
  );
}

function CardWithCount({
  count,
  icon,
  children,
}: {
  count: number;
  icon: typeof Inbox;
  children: React.ReactNode;
}) {
  const title = useSellerLabel('rfqs_open_title', { count });
  return <RailCard title={title} icon={icon}>{children}</RailCard>;
}

function UrgencyStrip({
  overdueCount,
  todayCount,
  className,
}: {
  overdueCount: number;
  todayCount: number;
  className?: string;
}) {
  const overdueLabel = useSellerLabel('rfqs_open_overdue', { count: overdueCount });
  const todayLabel = useSellerLabel('rfqs_open_today', { count: todayCount });
  return (
    <div className={className}>
      {overdueCount > 0 && (
        <span className="flex items-center gap-1 text-destructive">
          <AlertTriangle className="h-3 w-3" />
          {overdueLabel}
        </span>
      )}
      {todayCount > 0 && (
        <span className="flex items-center gap-1 text-yellow-600 dark:text-yellow-400">
          <Clock className="h-3 w-3" />
          {todayLabel}
        </span>
      )}
    </div>
  );
}

function RfqRow({
  rfq,
  onClick,
}: {
  rfq: VPRfqSummary & { _urgency: Urgency };
  onClick: () => void;
}) {
  const noProjectLabel = useSellerLabel('rfq_card_no_project');
  const linesLabel = useSellerLabel(
    rfq.line_count === 1 ? 'rfq_card_lines_one' : 'rfq_card_lines',
    { count: rfq.line_count },
  );

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/40 transition-colors border-t border-border/40"
    >
      <UrgencyDot urgency={rfq._urgency} />
      <div className="flex-1 min-w-0">
        <div className={cn(text.title, 'truncate')}>{rfq.reference ?? '—'}</div>
        <div className={cn(text.muted, 'truncate')}>
          {rfq.project_name ?? noProjectLabel}
          {rfq.line_count > 0 && ` · ${linesLabel}`}
        </div>
      </div>
      {rfq.deadline && (
        <span className={cn(text.muted, 'shrink-0')}>
          {new Date(rfq.deadline).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
        </span>
      )}
    </button>
  );
}

function UrgencyDot({ urgency }: { urgency: Urgency }) {
  const cls =
    urgency === 'overdue'
      ? 'bg-destructive'
      : urgency === 'today'
        ? 'bg-yellow-500'
        : urgency === 'soon'
          ? 'bg-blue-500'
          : 'bg-muted-foreground/40';
  return <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${cls}`} />;
}
